import { Component } from '@theme/component';
import {
  fetchNotes,
  guestNotes,
  isLoggedIn,
  saveGuestNote,
  saveNote,
  sanitizeNote,
  sanitizePublicNote,
} from '@theme/wishlist';

const CELL = '[data-wishlist-item]';

class WishlistNotes extends Component {
  #abort = null;
  #editing = null;
  #openedPublic = '';

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.#dialog?.addEventListener('close', this.#onCancelled, { signal });

    void this.#hydrate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  get #listId() {
    return this.dataset.listId || undefined;
  }

  #say(message) {
    const region = this.querySelector('[data-note-live]');
    if (!region || !message) return;

    // Cleared first: a region re-set to the text it already holds is not re-announced.
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  async #hydrate() {
    if (!isLoggedIn()) {
      this.#apply(guestNotes(), {});
      return;
    }

    try {
      const { notes, public_notes: publicNotes } = await fetchNotes(this.#listId);
      if (this.isConnected) this.#apply(notes ?? {}, publicNotes ?? {});
    } catch {
      // Rendering every note as empty after a failed read is worse than showing nothing: the
      // button reads "Add note" for an item that HAS one, and saving silently overwrites it.
      if (this.isConnected) this.#blockEditing();
    }
  }

  /** Server state is unknown, so editing is withheld rather than guessed at. */
  #blockEditing() {
    const message = this.dataset.unavailableText;
    for (const toggle of this.querySelectorAll('[data-note-edit]')) {
      toggle.setAttribute('aria-disabled', 'true');
      // A shopper who tabs here later needs the reason, not a silently inert control.
      toggle.title = message ?? '';
    }
    this.#say(message);
  }

  #apply(notes, publicNotes) {
    for (const cell of this.querySelectorAll(CELL)) {
      const key = cell.dataset.wishlistItem;
      this.#render(cell, notes[key] ?? '', publicNotes[key] ?? '');
    }
  }

  #publicNoteOf(cell) {
    return cell?.querySelector('[data-public-note-text]')?.textContent ?? '';
  }

  #render(cell, note, publicNote = '') {
    const shared = cell.querySelector('[data-public-note]');
    const sharedText = cell.querySelector('[data-public-note-text]');
    if (sharedText) sharedText.textContent = publicNote;
    if (shared instanceof HTMLElement) shared.hidden = !publicNote;

    const text = cell.querySelector('[data-note-text]');
    const toggle = cell.querySelector('[data-note-edit]');
    const label = toggle?.querySelector('[data-note-label]');
    if (!text || !toggle) return;

    // textContent, never innerHTML: this is the only place a note enters the page, and it must
    // stay data. The note is never rendered through Liquid — see the Worker's sanitizeNote.
    // The row keeps its height whether or not a note exists (layout), but an empty one is taken
    // out of the a11y tree so it is not a blank stop in browse-mode navigation.
    text.textContent = note;
    // setAttribute, not toggleAttribute: the latter writes aria-hidden="", which is not a valid
    // token, so the empty row is not reliably taken out of the a11y tree.
    text.setAttribute('aria-hidden', String(!note));

    const written = Boolean(note || publicNote);
    if (label) label.textContent = written ? toggle.dataset.shortEdit : toggle.dataset.shortAdd;
    toggle.setAttribute('aria-label', written ? toggle.dataset.labelEdit : toggle.dataset.labelAdd);
  }

  #onClick = (event) => {
    const cancel = event.target.closest('[data-note-cancel]');
    if (cancel && this.contains(cancel)) {
      event.preventDefault();
      this.#close();
      return;
    }

    const remove = event.target.closest('[data-note-remove]');
    if (remove && this.contains(remove)) {
      event.preventDefault();
      void this.#save(this.#editing, '', this.#publicField ? '' : undefined);
      return;
    }

    const toggle = event.target.closest('[data-note-edit]');
    if (!toggle || !this.contains(toggle)) return;

    event.preventDefault();
    if (toggle.getAttribute('aria-disabled') === 'true') return;
    this.#open(toggle.closest(CELL));
  };

  #onCancelled = () => {
    this.#close();
  };

  #onSubmit = (event) => {
    const form = event.target.closest('[data-note-form]');
    if (!form || !this.contains(form)) return;

    event.preventDefault();
    const publicValue = this.#publicField?.value;
    const changedPublic = publicValue !== undefined && publicValue.trim() !== this.#openedPublic ? publicValue : undefined;
    void this.#save(this.#editing, form.querySelector('[data-note-field]')?.value ?? '', changedPublic);
  };

  get #dialog() {
    return this.querySelector('[data-note-dialog]');
  }

  get #publicField() {
    return this.#dialog?.querySelector('[data-public-note-field]') ?? null;
  }

  #open(cell) {
    const dialog = this.#dialog;
    if (!cell || !dialog) return;

    this.#editing = cell;
    const current = cell.querySelector('[data-note-text]')?.textContent ?? '';

    const product = dialog.querySelector('[data-note-product]');
    if (product) product.textContent = cell.querySelector('[data-wishlist-note]')?.dataset.title ?? '';

    const field = dialog.querySelector('[data-note-field]');
    if (field) field.value = current;

    const currentPublic = this.#publicNoteOf(cell);
    this.#openedPublic = currentPublic;
    if (this.#publicField) this.#publicField.value = currentPublic;

    const remove = dialog.querySelector('[data-note-remove]');
    if (remove) remove.hidden = !(current || currentPublic);

    dialog.showModal();
    field?.focus();
    if (field) field.setSelectionRange(current.length, current.length);
  }

  #close() {
    const dialog = this.#dialog;
    const toggle = this.#editing?.querySelector('[data-note-edit]');

    if (dialog?.open) dialog.close();
    this.#editing = null;
    toggle?.focus();
  }

  async #save(cell, raw, rawPublic) {
    if (!cell) return;
    const note = sanitizeNote(raw);
    const [pid, vid] = (cell.dataset.wishlistItem ?? '').split(':');
    const previous = cell.querySelector('[data-note-text]')?.textContent ?? '';
    const previousPublic = this.#publicNoteOf(cell);
    const publicNote = rawPublic === undefined || !isLoggedIn() ? undefined : sanitizePublicNote(rawPublic);

    this.#render(cell, note, publicNote ?? previousPublic);
    this.#close();

    try {
      if (isLoggedIn()) {
        const result = await saveNote(pid, vid, note, this.#listId, publicNote);
        if (publicNote !== undefined && result?.public_note !== publicNote) throw new Error('public_note_not_saved');
      } else if (saveGuestNote(pid, vid, note) === null) throw new Error('guest_write_failed');
      this.#say(note || publicNote ? this.dataset.savedText : this.dataset.removedText);
    } catch (error) {
      if (!this.isConnected) return;
      this.#render(cell, previous, previousPublic);
      // A note is what usually tips a wishlist over the metafield's byte ceiling — it is the
      // largest and most variable part of an item. Saying "full" tells the shopper what to do;
      // the generic message leaves them retrying a save that cannot succeed.
      const full = error?.code === 'wishlist_too_large';
      this.#say(full ? this.dataset.fullText : this.dataset.errorText);
    }
  }
}

if (!customElements.get('wishlist-notes')) {
  customElements.define('wishlist-notes', WishlistNotes);
}
